import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CalendarDays, Clock, MapPin, Award, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [registered, setRegistered] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/events/${id}`).then((r) => setEvent(r.data)).catch(() => setError("Event not found."));
    if (user) {
      api.get(`/events/${id}/rsvp-status`).then((r) => setRegistered(r.data.registered)).catch(() => {});
    }
  }, [id, user]);

  const handleRegister = async () => {
    if (!user) { navigate("/login", { state: { redirect: `/events/${id}` } }); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/events/rsvp", { event_id: id });
      setRegistered(true);
      setEvent((e) => e ? { ...e, attendees: (e.attendees || 0) + (data.status === "registered" ? 1 : 0) } : e);
      toast.success("You're registered. Check your email for details.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (error) return <div className="max-w-3xl mx-auto py-24 px-6 text-center text-[color:var(--azsam-ink-soft)]">{error}</div>;
  if (!event) return <div className="max-w-3xl mx-auto py-24 px-6 text-center text-[color:var(--azsam-ink-soft)]" data-testid="event-loading">Loading…</div>;

  return (
    <div data-testid="event-detail">
      <div className="relative h-[380px] w-full overflow-hidden">
        <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--azsam-ink)]/85 via-[color:var(--azsam-ink)]/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-10 text-white">
            <Link to="/events" className="inline-flex items-center gap-2 text-sm opacity-90 hover:opacity-100" data-testid="event-back">
              <ArrowLeft size={16} /> All events
            </Link>
            <h1 className="mt-4 font-heading text-4xl sm:text-5xl max-w-4xl leading-tight">{event.title}</h1>
          </div>
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <div className="overline">About this event</div>
          <p className="mt-5 text-lg text-[color:var(--azsam-ink-soft)] leading-relaxed">{event.description}</p>
          {event.long_description && (
            <p className="mt-5 text-[color:var(--azsam-ink-soft)] leading-relaxed">{event.long_description}</p>
          )}
        </div>

        <aside className="lg:col-span-1">
          <div className="azsam-card p-8 sticky top-28">
            <div className="space-y-5 text-sm">
              <Detail icon={CalendarDays} label="Date" value={formatDate(event.date)} />
              {event.time && <Detail icon={Clock} label="Time" value={event.time} />}
              <Detail icon={MapPin} label="Location" value={event.location} />
              <Detail icon={MapPin} label="Format" value={event.format} />
              {event.cme_credits > 0 && <Detail icon={Award} label="CME" value={`${event.cme_credits} credits`} />}
            </div>
            {(event.capacity > 0 || event.attendees > 0) && (
              <div className="mt-6 pt-6 border-t border-[color:var(--azsam-stone)]">
                <div className="text-xs text-[color:var(--azsam-ink-soft)]">Registered</div>
                <div className="font-heading text-2xl mt-1">{event.attendees}/{event.capacity || "∞"}</div>
              </div>
            )}
            {registered ? (
              <div className="mt-6 flex items-center gap-2 text-[color:var(--azsam-sage-hover)] font-medium" data-testid="event-registered">
                <CheckCircle2 size={18} /> You are registered
              </div>
            ) : event.external_url ? (
              <a
                href={event.external_url}
                target="_blank"
                rel="noreferrer"
                className="btn-primary mt-6 w-full justify-center"
                data-testid="event-external-link"
              >
                Register on official site ↗
              </a>
            ) : (
              <button
                onClick={handleRegister}
                disabled={submitting}
                className="btn-primary mt-6 w-full justify-center disabled:opacity-60"
                data-testid="event-register-btn"
              >
                {submitting ? "Registering…" : user ? "Register for event" : "Log in to register"}
              </button>
            )}
          </div>
        </aside>
      </section>
    </div>
  );
}

function Detail({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={18} className="mt-0.5 text-[color:var(--azsam-clay)]" strokeWidth={1.6} />
      <div>
        <div className="text-xs uppercase tracking-wider text-[color:var(--azsam-ink-soft)]">{label}</div>
        <div className="text-[color:var(--azsam-ink)] font-medium">{value}</div>
      </div>
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  } catch { return iso; }
}
